import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { repoApi } from '@/lib/api';
import { repoBranchKeys } from './useRepoBranches';
import type { GitBranch, Repo } from 'shared/types';

export type RepoBranchConfig = {
  repoId: string;
  repoDisplayName: string;
  targetBranch: string | null;
  branches: GitBranch[];
  isSelected: boolean;
};

type UseRepoBranchSelectionOptions = {
  repos: Repo[];
  initialBranch?: string | null;
  enabled?: boolean;
};

type UseRepoBranchSelectionReturn = {
  configs: RepoBranchConfig[];
  isLoading: boolean;
  selectedRepoIds: Set<string>;
  toggleRepo: (repoId: string) => void;
  selectAll: () => void;
  selectNone: () => void;
  isSelected: (repoId: string) => boolean;
  setRepoBranch: (repoId: string, branch: string) => void;
  getWorkspaceRepoInputs: () => Array<{
    repo_id: string;
    target_branch: string;
  }>;
  reset: () => void;
};

export function useRepoBranchSelection({
  repos,
  initialBranch,
  enabled = true,
}: UseRepoBranchSelectionOptions): UseRepoBranchSelectionReturn {
  const [userOverrides, setUserOverrides] = useState<
    Record<string, string | null>
  >({});

  // All repos selected by default (backward compatible)
  const [selectedRepoIds, setSelectedRepoIds] = useState<Set<string>>(new Set());

  // Track previous repo IDs to detect when repos actually change
  const prevRepoIdsKeyRef = useRef<string>('');

  // Initialize/reset selectedRepoIds only when the repo list changes
  const currentRepoIdsKey = repos.map((r) => r.id).sort().join(',');

  useEffect(() => {
    // Only initialize/reset when repos list actually changes
    if (prevRepoIdsKeyRef.current !== currentRepoIdsKey && repos.length > 0) {
      prevRepoIdsKeyRef.current = currentRepoIdsKey;
      setSelectedRepoIds(new Set(repos.map((r) => r.id)));
    }
  }, [currentRepoIdsKey, repos]);

  const queries = useQueries({
    queries: repos.map((repo) => ({
      queryKey: repoBranchKeys.byRepo(repo.id),
      queryFn: () => repoApi.getBranches(repo.id),
      enabled,
      staleTime: 60_000,
    })),
  });

  const isLoadingBranches = queries.some((q) => q.isLoading);

  const configs = useMemo((): RepoBranchConfig[] => {
    return repos.map((repo, i) => {
      const branches = queries[i]?.data ?? [];
      const isSelected = selectedRepoIds.has(repo.id);

      let targetBranch: string | null = userOverrides[repo.id] ?? null;

      if (targetBranch === null) {
        if (initialBranch && branches.some((b) => b.name === initialBranch)) {
          targetBranch = initialBranch;
        } else {
          const currentBranch = branches.find((b) => b.is_current);
          targetBranch = currentBranch?.name ?? branches[0]?.name ?? null;
        }
      }

      return {
        repoId: repo.id,
        repoDisplayName: repo.display_name,
        targetBranch,
        branches,
        isSelected,
      };
    });
  }, [repos, queries, userOverrides, initialBranch, selectedRepoIds]);

  const toggleRepo = useCallback((repoId: string) => {
    setSelectedRepoIds((prev) => {
      const next = new Set(prev);
      if (next.has(repoId)) {
        next.delete(repoId);
      } else {
        next.add(repoId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedRepoIds(new Set(repos.map((r) => r.id)));
  }, [repos]);

  const selectNone = useCallback(() => {
    setSelectedRepoIds(new Set());
  }, []);

  const isSelected = useCallback(
    (repoId: string) => selectedRepoIds.has(repoId),
    [selectedRepoIds]
  );

  const setRepoBranch = useCallback((repoId: string, branch: string) => {
    setUserOverrides((prev) => ({
      ...prev,
      [repoId]: branch,
    }));
  }, []);

  const reset = useCallback(() => {
    setUserOverrides({});
    setSelectedRepoIds(new Set(repos.map((r) => r.id)));
  }, [repos]);

  const getWorkspaceRepoInputs = useCallback(() => {
    return configs
      .filter((config) => config.isSelected && config.targetBranch !== null)
      .map((config) => ({
        repo_id: config.repoId,
        target_branch: config.targetBranch!,
      }));
  }, [configs]);

  return {
    configs,
    isLoading: isLoadingBranches,
    selectedRepoIds,
    toggleRepo,
    selectAll,
    selectNone,
    isSelected,
    setRepoBranch,
    getWorkspaceRepoInputs,
    reset,
  };
}